/**
 * usePushSubscription — Setup completo de Web Push para la PWA.
 *
 * Al primer login del guardia:
 *   1. Registra /guard-sw.js como Service Worker
 *   2. Pide permiso al usuario
 *   3. Llama a /api/push/vapid-public para obtener la clave
 *   4. Suscribe el browser con esa clave
 *   5. POST a /api/push/subscription con el resultado
 *
 * Devuelve { state, enable, disable, sendTest }
 *   state: 'unsupported' | 'pending' | 'granted' | 'denied' | 'enabled'
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthStore } from '../store/authStore'

const SW_URL = '/guard-sw.js'

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw     = atob(base64)
  return new Uint8Array([...raw].map(c => c.charCodeAt(0)))
}

export function usePushSubscription() {
  const token = useAuthStore(s => s.token)
  const [state, setState] = useState('pending')
  const regRef = useRef(null)

  // Verificar soporte + estado inicial
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setState('unsupported')
      return
    }
    if (Notification.permission === 'denied') {
      setState('denied')
      return
    }
    if (Notification.permission === 'granted') {
      // Ya tenemos permiso — verificar suscripción
      checkSubscription()
    } else {
      setState('pending')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const ensureRegistration = async () => {
    if (regRef.current) return regRef.current
    const reg = await navigator.serviceWorker.register(SW_URL, { scope: '/' })
    regRef.current = reg
    await navigator.serviceWorker.ready
    return reg
  }

  const checkSubscription = async () => {
    try {
      const reg = await ensureRegistration()
      const sub = await reg.pushManager.getSubscription()
      setState(sub ? 'enabled' : 'granted')
    } catch (e) {
      console.warn('[usePush] checkSubscription error', e)
      setState('pending')
    }
  }

  const enable = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return false
    if (!token) return false

    try {
      // 1. Pedir permiso si todavía no
      let perm = Notification.permission
      if (perm === 'default') {
        perm = await Notification.requestPermission()
      }
      if (perm !== 'granted') {
        setState(perm === 'denied' ? 'denied' : 'pending')
        return false
      }

      // 2. Registrar SW
      const reg = await ensureRegistration()

      // 3. Obtener clave VAPID pública del backend
      const vRes = await fetch('/api/push/vapid-public', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!vRes.ok) throw new Error(`vapid ${vRes.status}`)
      const { public_key } = await vRes.json()

      // 4. Suscribir
      let sub = await reg.pushManager.getSubscription()
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(public_key),
        })
      }

      // 5. Enviar al backend
      const ua = navigator.userAgent.slice(0, 100)
      const r = await fetch('/api/push/subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization : `Bearer ${token}`,
        },
        body: JSON.stringify({
          subscription: sub.toJSON(),
          device_label: ua,
        }),
      })
      if (!r.ok) throw new Error(`subscribe ${r.status}`)
      setState('enabled')
      return true
    } catch (e) {
      console.warn('[usePush] enable error', e)
      return false
    }
  }, [token])

  const disable = useCallback(async () => {
    try {
      const reg = await ensureRegistration()
      const sub = await reg.pushManager.getSubscription()
      if (sub) await sub.unsubscribe()
      setState('granted')
    } catch (e) {
      console.warn('[usePush] disable error', e)
    }
  }, [])

  const sendTest = useCallback(async () => {
    if (!token) return false
    try {
      const r = await fetch('/api/push/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization : `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: '🚨 Prueba de notificación',
          body : 'Si recibís esto, el push está funcionando correctamente.',
        }),
      })
      const d = await r.json().catch(() => ({}))
      return d.sent > 0
    } catch (e) {
      console.warn('[usePush] test error', e)
      return false
    }
  }, [token])

  return { state, enable, disable, sendTest }
}
